import React, { useEffect, useState } from "react";
import { API_BASE } from "../api/config";
import LocationSelect from "../components/LocationSelect";
import "./NegativeStockPanel.css";

function formatQty(value) {
  return Number(value || 0).toLocaleString();
}

function formatMoney(value) {
  return Number(value || 0).toLocaleString();
}

export default function NegativeStockPanel({ onRequestCreated }) {
  const [location, setLocation] = useState("ALL");
  const [brand, setBrand] = useState("ALL");
  const [productCode, setProductCode] = useState("");
  const [rows, setRows] = useState([]);
  const [correctQtyMap, setCorrectQtyMap] = useState({});
  const [reasonMap, setReasonMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState("");
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  function rowKey(row) {
    return `${row.locationCode}|||${row.productCode}|||${row.color || ""}|||${row.size || ""}`;
  }

  async function loadNegativeStock() {
    try {
      setLoading(true);
      setError("");
      setSuccessMsg("");

      const query = new URLSearchParams();
      query.set("locationCode", location || "ALL");
      query.set("brand", brand || "ALL");

      if (productCode.trim()) {
        query.set("productCode", productCode.trim());
      }

      const response = await fetch(`${API_BASE}/api/erp/negative-stock?${query.toString()}`);
      const data = await response.json();

      if (!response.ok || data.success === false) {
        throw new Error(data.message || "Failed to load negative stock");
      }

      const nextRows = Array.isArray(data.rows) ? data.rows : [];
      setRows(nextRows);

      const nextQty = {};
      const nextReason = {};

      nextRows.forEach((row) => {
        const key = rowKey(row);
        nextQty[key] = "0";
        nextReason[key] = "Negative stock correction request";
      });

      setCorrectQtyMap(nextQty);
      setReasonMap(nextReason);
    } catch (err) {
      setError(err.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  async function createRequest(row) {
    const key = rowKey(row);

    try {
      setSavingKey(key);
      setError("");
      setSuccessMsg("");

      const correctQty = Number(correctQtyMap[key] || 0);

      const response = await fetch(`${API_BASE}/api/erp/stock-adjustment-requests`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          locationCode: row.locationCode,
          productCode: row.productCode,
          color: row.color || "",
          size: row.size || "",
          brand: row.brand || "",
          requestedQty: correctQty,
          reason: reasonMap[key] || "Negative stock correction request",
          requestedBy: "OFFICE",
        }),
      });

      const data = await response.json();

      if (!response.ok || data.success === false) {
        throw new Error(data.message || "Failed to create request");
      }

      setSuccessMsg(`Request created: ${row.locationCode} / ${row.productCode} / ${row.color} / ${row.size}`);

      if (typeof onRequestCreated === "function") {
        onRequestCreated();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingKey("");
    }
  }

  useEffect(() => {
    loadNegativeStock();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, brand]);

  return (
    <div className="negativeStockPanel">
      <div className="negativeStockPanel__head">
        <div>
          <div className="negativeStockPanel__tag">NEGATIVE STOCK CONTROL</div>
          <h3>Negative Stock Report / Fix Request</h3>
          <p>
            Current stock negative ဖြစ်နေတဲ့ item တွေကိုစစ်ပြီး Fix Request တင်ရန်။
            ဒီအဆင့်မှာ Firebase / ERP stock မပြင်သေးပါ။
          </p>
        </div>

        <button type="button" onClick={loadNegativeStock} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      <div className="negativeStockPanel__filters">
        <label>
          <span>Location</span>
          <LocationSelect
            value={location}
            onChange={setLocation}
            type="ALL"
            includeAll={true}
            allLabel="All Locations"
          />
        </label>

        <label>
          <span>Brand</span>
          <select value={brand} onChange={(e) => setBrand(e.target.value)}>
            <option value="ALL">ALL</option>
            <option value="PRETTYFIT">PRETTYFIT</option>
            <option value="HANGTEN">HANGTEN</option>
          </select>
        </label>

        <label>
          <span>Search Code</span>
          <input
            value={productCode}
            onChange={(e) => setProductCode(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") loadNegativeStock();
            }}
            placeholder="Product code..."
          />
        </label>

        <button type="button" onClick={loadNegativeStock} disabled={loading}>
          Search
        </button>
      </div>

      {error && <div className="negativeStockPanel__error">{error}</div>}
      {successMsg && <div className="negativeStockPanel__success">{successMsg}</div>}

      <div className="negativeStockPanel__summary">
        Negative Stock Items: <b>{rows.length}</b>
      </div>

      <div className="negativeStockTableWrap">
        <table className="negativeStockTable">
          <thead>
            <tr>
              <th>Location</th>
              <th>Brand</th>
              <th>Code</th>
              <th>Color</th>
              <th>Size</th>
              <th>Current Qty</th>
              <th>Correct Qty</th>
              <th>Price</th>
              <th>Reason</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan="10" className="negativeStockTable__empty">
                  No negative stock found.
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const key = rowKey(row);

                return (
                  <tr key={key}>
                    <td>{row.locationCode}</td>
                    <td>{row.brand || "-"}</td>
                    <td className="negativeStockTable__code">{row.productCode}</td>
                    <td>{row.color || "-"}</td>
                    <td>{row.size || "-"}</td>
                    <td className="negativeStockTable__qty">{formatQty(row.currentQty)}</td>
                    <td>
                      <input
                        className="negativeStockTable__qtyInput"
                        type="number"
                        value={correctQtyMap[key] ?? "0"}
                        onChange={(e) =>
                          setCorrectQtyMap((prev) => ({
                            ...prev,
                            [key]: e.target.value,
                          }))
                        }
                      />
                    </td>
                    <td>{formatMoney(row.priceMmk)} Ks</td>
                    <td>
                      <input
                        className="negativeStockTable__reasonInput"
                        value={reasonMap[key] || ""}
                        onChange={(e) =>
                          setReasonMap((prev) => ({
                            ...prev,
                            [key]: e.target.value,
                          }))
                        }
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="negativeStockTable__requestBtn"
                        onClick={() => createRequest(row)}
                        disabled={savingKey === key}
                      >
                        {savingKey === key ? "Saving..." : "Create Request"}
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
